import React, {
  cloneElement,
  isValidElement,
  ReactElement,
  ReactNode,
} from "react";
import { useAuth } from "@/contexts/AuthContext";
import { RbacAction, RbacModule } from "@/rbac/modules";

interface PermissionGateProps {
  module: RbacModule;
  action: RbacAction;
  /** Render children only if permission is held. Default: hide */
  fallback?: ReactNode;
  /**
   * If true, renders children disabled instead of hiding them.
   * Works for <button>, <input>, or any element that accepts a `disabled` prop.
   */
  disableInstead?: boolean;
  children: ReactNode;
}

/**
 * Component-level guard. Hides (default) or disables its children when the
 * current admin lacks the given permission.
 */
export const PermissionGate: React.FC<PermissionGateProps> = ({
  module,
  action,
  fallback = null,
  disableInstead = false,
  children,
}) => {
  const { can } = useAuth();
  const allowed = can(module, action);

  if (allowed) return <>{children}</>;

  if (disableInstead && isValidElement(children)) {
    return cloneElement(children as ReactElement<any>, {
      disabled: true,
      "aria-disabled": true,
      onClick: undefined,
    });
  }

  return <>{fallback}</>;
};
